import React, { useState, useEffect, useContext, useRef } from "react";
import { IoClose } from "react-icons/io5";
import {
  FaCheck,
  FaTimes,
  FaMicrophone,
  FaVolumeUp,
  FaVideo,
} from "react-icons/fa";
import { PiWarningFill } from "react-icons/pi";
import { checkAllDevices, classifyMediaError } from "../services/web-RTC";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import {
  getMyTransports,
  loadDevice,
  sendTransportListenEvents,
  rejoindreMeeting,
} from "../services/mediasoup-client";
import { getTurnServers } from "../datas.config";
import { useMeeting } from "../hooks/useMeeting";
import { useMediasoupClient } from "../hooks/useMediasoupClient";
import { getMediasTracks } from "../services/web-RTC";
import DeviceErrorDialog from "./DeviceErrorDialog";

export default function ModalMedium({ theme, datas }) {
  const { accessCode, meetingId, displayModalMedium, setDisplayModalMedium } =
    datas;
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [mediaError, setMediaError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [display, setDisplay] = useState(displayModalMedium);
  const [color, setColor] = useState(theme === "dark" ? "#fff" : "#0256cd");
  const location = useNavigate();
  const { user } = useContext(AuthContext);
  const { setLocalStream, setMyPeer, joinMeetingSession } = useMeeting();
  const {
    setSendTransport,
    setRecvTransport,
    setProducerAudio,
    setProducerVideo,
  } = useMediasoupClient();
  const myDeviceRef = useRef(null);

  useEffect(() => {
    if (theme === "dark") {
      setColor("#fff");
    } else {
      setColor("#0256cd");
    }
  }, [theme]);

  const runDeviceCheck = async () => {
    setDeviceStatus(null);
    const status = await checkAllDevices();
    setDeviceStatus(status);
  };

  useEffect(() => {
    runDeviceCheck();
  }, []);

  const handleGetMyTransports = async (constraints) => {
    setLoading(true);
    setMediaError(null);
    try {
      const { RTP_CAPABILITIES_SERVER, sendTransport, recvTransport } =
        await getMyTransports(user.userId);
      const device = await loadDevice(RTP_CAPABILITIES_SERVER);
      myDeviceRef.current = device;
      const res = await rejoindreMeeting({ device, meetingId });
      const { displayName } = res.peer || {};
      const iceServers = await getTurnServers();
      const sendTrans = device.createSendTransport({
        ...sendTransport,
        iceServers,
      });
      const recvTrans = device.createRecvTransport({
        ...recvTransport,
        iceServers,
      });
      setSendTransport(sendTrans);
      setRecvTransport(recvTrans);
      sendTransportListenEvents(sendTrans);

      const { audioTrack, videoTrack, error } =
        await getMediasTracks(constraints);

      if (error) {
        setLoading(false);
        setMediaError(error);
        return;
      }

      const producerAudio = await sendTrans.produce({
        track: audioTrack,
        appData: {
          mediaTag: "audio",
          userId: user.userId,
          userImage: user.userImage,
          enabledVideo: videoTrack ? true : false,
        },
      });
      setProducerAudio(producerAudio);
      if (videoTrack) {
        const producerVideo = await sendTrans.produce({
          track: videoTrack,
          appData: {
            mediaTag: "video",
            userId: user.userId,
            userImage: user.userImage,
            enabledVideo: true,
          },
        });
        setProducerVideo(producerVideo);
      }
      const stream = videoTrack
        ? new MediaStream([audioTrack, videoTrack])
        : new MediaStream([audioTrack]);
      setLocalStream(stream);
      setMyPeer({
        mediaTag: constraints.video ? "video" : "audio",
        videoOn: constraints.video ? true : false,
        audioOn: true,
        kind: constraints.video ? "video" : "audio",
        stream,
        userId: user.userId,
        producerPaused: false,
        displayName:
          displayName.length > 20
            ? displayName.split(" ")[1] || displayName.split(" ")[0]
            : displayName,
        isLocal: true,
        userImage: user.userImage,
      });
      joinMeetingSession(meetingId, accessCode, constraints);
      location(`/reunion-start`);
    } catch (err) {
      console.error("Erreur handleGetMyTransports :", err);
      setLoading(false);
      setMediaError(classifyMediaError(err));
    }
  };

  const handleClose = () => {
    setDisplayModalMedium(false);
    setDisplay(false);
    location("/reunion");
  };

  const handleRetry = () => {
    setMediaError(null);
    setLoading(false);
    runDeviceCheck();
  };

  // Show media error dialog (getUserMedia runtime error)
  if (mediaError) {
    return (
      <DeviceErrorDialog
        type="media_error"
        mediaError={mediaError}
        theme={theme}
        onClose={handleClose}
        onRetry={handleRetry}
      />
    );
  }

  // Still loading device check
  if (!deviceStatus) {
    return null;
  }

  // Determine button accessibility based on device status
  const hasError = !!deviceStatus.error;
  const canUseMicAndCamera = deviceStatus.allAvailable && !hasError;
  const canUseMicOnly = deviceStatus.canJoinAudioOnly && !hasError;
  const hasAnyProblem = !deviceStatus.allAvailable || hasError;

  // Build the problems list for the inline warning
  const problems = [];
  if (hasError) {
    const errorMessages = {
      browser_unsupported:
        "Votre navigateur ne prend pas en charge la détection des périphériques.",
      insecure_context:
        "L'accès aux périphériques nécessite une connexion sécurisée (HTTPS).",
      permission_denied:
        "L'accès aux périphériques a été refusé par votre navigateur.",
      unknown:
        "Une erreur est survenue lors de la détection des périphériques.",
    };
    problems.push(
      errorMessages[deviceStatus.error] || errorMessages.unknown
    );
  }
  if (!deviceStatus.microphone.available) problems.push("Aucun microphone détecté");
  if (!deviceStatus.camera.available) problems.push("Aucune caméra détectée");

  const speakerWarning = !deviceStatus.speaker.available;

  const isDark = theme === "dark";

  return (
    <>
      {display ? (
        <div className="px-3" style={{ marginTop: "10%" }}>
          <div
            className="animate__animated animate__backInLeft modal fade show position-static d-block w-auto h-auto overflow-visible"
            tabIndex="-1"
            id="muteOptions-"
          >
            <div className="modal-dialog modal-dialog-centered my-0">
              <div className="modal-content border-0">
                <div
                  className="modal-body p-4"
                  style={{ position: "relative" }}
                >
                  <h6 className="pb-2 fs-3 fw-bolder" style={{ color }}>
                    Voulez-vous permettre aux autres de vous voir et entendre
                    lors de la réunion?
                  </h6>
                  <p className="fw-medium fs-5" style={{ color }}>
                    Vous pourrez toujours désactiver votre micro et votre caméra
                    à tout moment pendant la réunion.
                  </p>

                  {/* Device status inline */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      padding: "12px",
                      borderRadius: "10px",
                      marginBottom: "16px",
                      backgroundColor: isDark
                        ? "rgba(255,255,255,0.06)"
                        : "#f1f5f9",
                    }}
                  >
                    {[
                      {
                        icon: <FaMicrophone />,
                        label: "Microphone",
                        ok: deviceStatus.microphone.available,
                      },
                      {
                        icon: <FaVolumeUp />,
                        label: "Haut-parleur",
                        ok: deviceStatus.speaker.available,
                      },
                      {
                        icon: <FaVideo />,
                        label: "Caméra",
                        ok: deviceStatus.camera.available,
                      },
                    ].map((d) => (
                      <div
                        key={d.label}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "6px 8px",
                          borderRadius: "8px",
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "14px",
                            color: d.ok ? "#00b074" : "#ef4444",
                            width: "18px",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          {d.icon}
                        </span>
                        <span style={{ flex: 1 }}>{d.label}</span>
                        {d.ok ? (
                          <FaCheck
                            style={{ fontSize: "13px", color: "#00b074" }}
                          />
                        ) : (
                          <FaTimes
                            style={{ fontSize: "13px", color: "#ef4444" }}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Warning box if problems */}
                  {hasAnyProblem && problems.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        padding: "10px 14px",
                        borderRadius: "10px",
                        marginBottom: "16px",
                        backgroundColor: isDark
                          ? "rgba(239, 68, 68, 0.12)"
                          : "#fef2f2",
                        color: isDark ? "#fca5a5" : "#dc2626",
                        fontSize: "0.85rem",
                        lineHeight: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <PiWarningFill
                        style={{
                          fontSize: "18px",
                          flexShrink: 0,
                          marginTop: "2px",
                        }}
                      />
                      <div>
                        {problems.map((p, i) => (
                          <div key={i}>{p}</div>
                        ))}
                        {!canUseMicOnly && (
                          <div style={{ marginTop: "6px", fontWeight: 600 }}>
                            Branchez vos périphériques puis cliquez sur
                            « Réessayer ».
                          </div>
                        )}
                        {canUseMicOnly && !canUseMicAndCamera && (
                          <div style={{ marginTop: "6px", fontWeight: 600 }}>
                            Vous pouvez rejoindre avec le micro uniquement.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Speaker warning (non-blocking) */}
                  {speakerWarning && (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        padding: "10px 14px",
                        borderRadius: "10px",
                        marginBottom: "16px",
                        backgroundColor: isDark
                          ? "rgba(245, 158, 11, 0.12)"
                          : "#fffbeb",
                        color: isDark ? "#fcd34d" : "#b45309",
                        fontSize: "0.85rem",
                        lineHeight: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <PiWarningFill
                        style={{
                          fontSize: "18px",
                          flexShrink: 0,
                          marginTop: "2px",
                        }}
                      />
                      <div>
                        Aucun haut-parleur détecté. Vous pourrez rejoindre la
                        réunion, mais vous risquez de ne pas entendre les autres
                        participants.
                      </div>
                    </div>
                  )}

                  <div className="container">
                    {/* Micro + Camera button */}
                    <div className="row">
                      <div
                        onClick={() => {
                          if (canUseMicAndCamera && !loading)
                            handleGetMyTransports({
                              video: true,
                              audio: true,
                            });
                        }}
                        className={`col col-12 fs-3 ${
                          canUseMicAndCamera
                            ? "animate__animated animate__pulse animate__infinite"
                            : ""
                        }`}
                        style={{
                          height: "50px",
                          borderRadius: "25px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: canUseMicAndCamera
                            ? "#0256cd"
                            : "#6b7280",
                          cursor: canUseMicAndCamera
                            ? loading
                              ? "wait"
                              : "pointer"
                            : "not-allowed",
                          color: "#fff",
                          opacity: canUseMicAndCamera
                            ? loading
                              ? 0.7
                              : 1
                            : 0.5,
                        }}
                      >
                        {loading
                          ? "Connexion..."
                          : "Utiliser le micro et la caméra"}
                      </div>
                    </div>

                    {/* Micro only button */}
                    <div
                      className="row mt-4 d-flex"
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        onClick={() => {
                          if (canUseMicOnly && !loading)
                            handleGetMyTransports({
                              video: false,
                              audio: true,
                            });
                        }}
                        className="bg-transparent fs-3"
                        style={{
                          height: "50px",
                          width: "100%",
                          borderRadius: "25px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: canUseMicOnly
                            ? loading
                              ? "wait"
                              : "pointer"
                            : "not-allowed",
                          border: `1.4px solid ${
                            canUseMicOnly
                              ? isDark
                                ? "#fff"
                                : "#0256cd"
                              : "#6b7280"
                          }`,
                          color: canUseMicOnly ? color : "#6b7280",
                          opacity: canUseMicOnly
                            ? loading
                              ? 0.7
                              : 1
                            : 0.5,
                        }}
                      >
                        {loading
                          ? "Connexion..."
                          : "Utiliser le micro uniquement"}
                      </div>
                    </div>

                    {/* Retry button (only when problems) */}
                    {hasAnyProblem && (
                      <div className="row mt-4">
                        <div
                          onClick={handleRetry}
                          className="col col-12 fs-3"
                          style={{
                            height: "50px",
                            borderRadius: "25px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                            backgroundColor: isDark ? "#1e293b" : "#f1f5f9",
                            border: `1.4px solid ${
                              isDark ? "#475569" : "#cbd5e1"
                            }`,
                            color: isDark ? "#e2e8f0" : "#334155",
                          }}
                        >
                          Réessayer la détection
                        </div>
                      </div>
                    )}

                    {/* Back button */}
                    <div className="row mt-4">
                      <div
                        onClick={() => {
                          if (!loading) {
                            setDisplayModalMedium(false);
                            location("/reunion");
                          }
                        }}
                        className="col col-12 bg-transparent fs-3"
                        style={{
                          height: "50px",
                          borderRadius: "25px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: loading ? "wait" : "pointer",
                          border: `1.4px solid ${
                            isDark ? "#fff" : "#0256cd"
                          }`,
                          color,
                        }}
                      >
                        Reprendre l'opération
                      </div>
                    </div>
                  </div>
                  <button
                    className="js-chat-mute border-none"
                    style={{
                      position: "absolute",
                      top: "-15px",
                      right: "-15px",
                      borderRadius: "50%",
                      backgroundColor: "#ff4343",
                      color: "#fff",
                      height: "30px",
                      width: "30px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "25px",
                    }}
                    onClick={handleClose}
                  >
                    <IoClose />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
